/** Requerimientos: tarjetas con codigo + cliente + fecha + estado. */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileCard } from '@/components/MobileCard';
import { StatusBadge } from '@/components/StatusBadge';
import { SearchInput } from '@/components/SearchInput';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { PullIndicator } from '@/components/PullIndicator';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useMobileRequirementsQuery } from '@/hooks/useMobileRequirements';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { formatRelativeDate } from '@/utils/format';

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function RequirementsPage() {
  const { user, hasPermission } = useMobileAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const requirementsQuery = useMobileRequirementsQuery();
  const { bindRef, pulling, refreshing } = usePullToRefresh({
    onRefresh: () => requirementsQuery.refetch(),
    disabled: user?.active_tenant_id == null || !hasPermission('customer_requirements.view'),
  });

  const filtered = useMemo(() => {
    const items = requirementsQuery.data?.items ?? [];
    const mine = showAll
      ? items
      : items.filter(
          (item) =>
            (item.commercial_owner_id && item.commercial_owner_id === user?.id) ||
            (item.created_by && item.created_by === user?.id),
        );
    if (!search.trim()) return mine;
    const q = search.trim().toLowerCase();
    return mine.filter(
      (item) =>
        item.code.toLowerCase().includes(q) ||
        item.name.toLowerCase().includes(q) ||
        (item.client?.legal_name ?? '').toLowerCase().includes(q),
    );
  }, [requirementsQuery.data, search, showAll, user?.id]);

  return (
    <MobileLayout
      title="Requerimientos"
      subtitle={`${filtered.length} de ${requirementsQuery.data?.total ?? 0} en total`}
    >
      <div ref={bindRef} className="space-y-4">
        <PullIndicator pulling={pulling} refreshing={refreshing} />
        {hasPermission('customer_requirements.create') && (
          <Link to="/m/requirements/new">
            <MobileButton
              variant="primary"
              size="md"
              fullWidth
              icon={<MobileIcon name="add" className="h-5 w-5" />}
            >
              Nuevo requerimiento
            </MobileButton>
          </Link>
        )}

        <div
          role="radiogroup"
          aria-label="Filtrar requerimientos por autor"
          className="flex items-center gap-1 rounded-full bg-bg-tertiary p-1"
        >
          {(
            [
              { value: false, label: 'Solo míos' },
              { value: true, label: 'Todos' },
            ] as const
          ).map((option) => (
            <button
              key={option.label}
              type="button"
              role="radio"
              aria-checked={showAll === option.value}
              onClick={() => setShowAll(option.value)}
              className={clsx(
                'flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full text-sm font-semibold transition-all active:scale-[0.97]',
                showAll === option.value
                  ? 'bg-white text-text-primary shadow-card'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar por código, nombre o cliente..."
        />

        {requirementsQuery.isPending && <CardListSkeleton count={4} />}

        {requirementsQuery.isError && (
          <EmptyState
            icon="error"
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            onAction={() => requirementsQuery.refetch()}
          />
        )}

        {!requirementsQuery.isPending && !requirementsQuery.isError && filtered.length === 0 && (
          <EmptyState
            icon={search || showAll ? 'search_off' : 'assignment'}
            title={search ? 'Sin resultados' : 'Sin requerimientos'}
            description={
              search
                ? 'Ninguno coincide con tu búsqueda.'
                : showAll
                  ? 'Aún no hay requerimientos.'
                  : 'Aún no tienes requerimientos asignados.'
            }
          />
        )}

        <div className="space-y-3">
          {filtered.map((item) => (
            <MobileCard
              key={item.id}
              title={item.code || item.name}
              subtitle={item.code ? item.name : undefined}
              icon="assignment"
              iconColor="success"
              rightContent={<StatusBadge status={item.status} size="sm" />}
              metadata={[
                ...(item.client?.legal_name
                  ? [{ label: 'Cliente', value: item.client.legal_name }]
                  : []),
                ...(item.required_date
                  ? [
                      {
                        label: 'Requerido',
                        value: `${formatDate(item.required_date)} · ${formatRelativeDate(item.required_date)}`,
                      },
                    ]
                  : []),
              ]}
              onClick={() => navigate(`/m/requirements/${item.id}`)}
            />
          ))}
        </div>
      </div>
    </MobileLayout>
  );
}
